export const sanitizeCustomTags = (
  content: string,
  previousChat: any,
  steps: boolean = true,
) => {
  if (!content) return "";

  // // If steps is false, return content without sanitization
  // if (!steps) {
  //   return content;
  // }

  if (
    content.toLowerCase().includes("<invoke") &&
    !content.toLowerCase().includes("</invoke>")
  ) {
    return "";
  }
  if (
    content.toLowerCase().includes("<function_calls") &&
    !content.toLowerCase().includes("</function_calls>")
  ) {
    return "";
  }
  if (
    content.toLowerCase().includes("<tools_results") &&
    !content.toLowerCase().includes("</tools_results>")
  ) {
    return "";
  }
  if (
    content.toLowerCase().includes("<reasoning-cbb") &&
    !content.toLowerCase().includes("</reasoning-cbb>")
  ) {
    return "";
  }

  const customTags = [
    "<br-cbb>",
    "<links-cbb>",
    "</links-cbb>",
    "<clickable-options-cbb>",
    "</clickable-options-cbb>",
    "<handoff>",
    "<ecommerce-image>",
    "</ecommerce-image>",
  ];

  // First, strip COMPLETE inline tags that have both opening and closing
  // These appear inline in the message and surrounding text should be preserved
  let result = content.replace(/<links-cbb>([\s\S]*?)<\/links-cbb>/gi, "");

  result = result.replace(
    /<clickable-options-cbb[^>]*>[\s\S]*?<\/clickable-options-cbb>/gi,
    "",
  );

  // Tags that should cause truncation (they appear at the END of the message)
  const truncationTags = [
    "<br-cbb>",
    "<clickable-options-cbb>",
    "</clickable-options-cbb>",
    "<handoff>",
  ];

  // Check for incomplete inline tags (opening without closing - streaming scenario)
  // If opening tag exists without closing, we need to truncate from that point
  if (
    result.toLowerCase().includes("<links-cbb>") &&
    !result.toLowerCase().includes("</links-cbb>")
  ) {
    truncationTags.push("<links-cbb>");
  }

  let truncateIndex = result.length;
  for (const tag of truncationTags) {
    const index = result.toLowerCase().indexOf(tag.toLowerCase());
    if (index !== -1 && index > 0 && index < truncateIndex) {
      truncateIndex = index;
    }
  }

  result = result.substring(0, truncateIndex);

  // Remove any remaining custom tags
  customTags.forEach((tag) => {
    result = result.replace(
      new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
      "",
    );
  });

  if (previousChat) {
    // Version 1 behavior: Convert markdown-style lists to proper HTML if not already HTML
    if (!result.includes("<li>") && !result.includes("<ul>")) {
      // Convert markdown * bullets to HTML lists
      result = result.replace(/^\*\s+(.+)$/gm, "<li>$1</li>");
      // Wrap consecutive li tags in ul
      result = result.replace(
        /(<li>.*?<\/li>(?:\s*<li>.*?<\/li>)*)/gs,
        "<ul>$1</ul>",
      );
      // Convert **text** to <strong>text</strong>
      result = result.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    }
    // Keep HTML structure intact (don't remove li tags)
  } else {
    // Version 2 behavior: Remove li tags
    result = result.replace(/<li>|<\/li>/g, "");
  }

  result = result.replace(/<invoke[^>]*>[\s\S]*<\/?invoke[^>]*>/gi, "");
  result = result.replace(/<function[^>]*>[\s\S]*<\/?function[^>]*>/gi, "");
  result = result.replace(/<tool[^>]*>[\s\S]*<\/?tool[^>]*>/gi, "");
  result = result.replace(
    /<reasoning-cbb[^>]*>[\s\S]*<\/?reasoning-cbb[^>]*>/gi,
    "",
  );
  // Remove tool information like {"tool_name": "product_search", "tool_id": 0} or {"tool_name": "product_search", "tool_id": "1/2/3"}
  result = result.replace(
    /\{"tool_name":\s*"[^"]*",\s*"tool_id":\s*(?:\d+|"[^"]*")\}/g,
    "",
  );

  result = result.replace(/<\/?br-cbb>/gi, " ");
  result = result.replace(/<\/?strong>/gi, "**");
  result = result.replace(/<\/?result[^>]*>/gi, "");
  result = result.replace(/<\/?fallback[^>]*>/gi, "");

  return result;
};

export const extractSeperationData = (
  content: string,
  tagName: string = "br-cbb",
  previousChat: any,
  steps: boolean = true,
) => {
  const tag = `<${tagName}>`;
  const pattern = new RegExp(tag, "g");
  const matches = content.split(pattern);

  const filteredMatches = matches
    .map((match) => sanitizeCustomTags(match, previousChat, steps))
    .filter((match) => match.trim() !== "");
  return filteredMatches.slice(1);
};

export const extractClickableOptionsData = (
  content: string,
  tagName: string,
  previousChat: any,
  steps: boolean = true,
) => {
  if (!content || typeof content !== "string") return null;

  const pattern = new RegExp(`<${tagName}[^>]*>(.*?)<\/${tagName}>`, "s");
  const match = content.match(pattern);

  if (!match || !match[1]) {
    return null;
  }

  const liPattern = /<li[^>]*>(.*?)<\/li>/g;
  const liMatches = [...match[1].matchAll(liPattern)];

  if (liMatches.length === 0) {
    return null;
  }

  if (previousChat) {
    // Version 1 behavior: Remove ALL HTML tags for clean clickable option text
    return liMatches.map((match) => {
      let cleanText = match[1].trim();
      // Remove any remaining HTML tags for clean clickable option text
      cleanText = cleanText.replace(/<[^>]*>/g, "");
      return cleanText;
    });
  } else {
    // Version 2 behavior: Use sanitizeCustomTags function
    return liMatches.map((match) =>
      sanitizeCustomTags(match[1].trim(), previousChat, steps),
    );
  }
};

export const extractMediaLinks = (content: string): string[] => {
  if (!content || typeof content !== "string") return [];

  const pattern = /<links-cbb[^>]*>(.*?)<\/links-cbb>/s;
  const match = content.match(pattern);

  if (!match || !match[1]) {
    return [];
  }

  const liPattern = /<li[^>]*>(.*?)<\/li>/g;
  const liMatches = [...match[1].matchAll(liPattern)];

  return liMatches.map((match) => match[1].trim()).filter((link) => link);
};

export const extractEcommerceData = (content: string): any[] => {
  if (!content || typeof content !== "string") {
    console.log("❌ extractEcommerceData: No content provided");
    return [];
  }

  // Try both tag formats
  const pattern = /<ecommerce[-_]image[^>]*>(.*?)<\/ecommerce[-_]image>/is;
  const match = content.match(pattern);

  if (!match || !match[1]) {
    console.log("❌ extractEcommerceData: No ecommerce-image tag found");
    return [];
  }

  const innerContent = match[1].trim();
  console.log("📝 extractEcommerceData: Inner content:", innerContent);

  // Try to parse as JSON first (most common format)
  try {
    // Look for JSON array or object
    const jsonMatch = innerContent.match(/(\[[\s\S]*\]|\{[\s\S]*\})/);
    if (jsonMatch) {
      console.log("🔍 Found JSON content, attempting parse...");
      const parsed = JSON.parse(jsonMatch[0]);
      const products = Array.isArray(parsed) ? parsed : [parsed];
      console.log("✅ Parsed products from JSON:", products);
      return products;
    }
  } catch (e) {
    console.log("⚠️ JSON parsing failed, trying Markdown format...");
  }

  // Parse Markdown format with images
  // Format: - ![name](image_url)\n  **[name](link)** - $price\n  description
  const markdownPatternWithImage =
    /-\s*!\[([^\]]*)\]\(([^)]+)\)\s*\*\*\[([^\]]*)\]\(([^)]+)\)\*\*\s*-\s*([^\n]+)\s*([^\n-]*)/g;
  const markdownMatchesWithImage = [
    ...innerContent.matchAll(markdownPatternWithImage),
  ];

  if (markdownMatchesWithImage.length > 0) {
    console.log(
      `✅ Found ${markdownMatchesWithImage.length} products in Markdown format (with images)`,
    );
    const products = markdownMatchesWithImage.map((match, idx) => {
      const product = {
        name: match[3] || match[1], // Product name from link or image alt
        image: match[2], // Image URL
        link: match[4], // Product link
        price: match[5].trim(), // Price
        description: match[6]?.trim() || "", // Description
      };
      console.log(`📦 Product ${idx + 1}:`, product);
      return product;
    });
    return products;
  }

  // Parse Markdown format WITHOUT images (simpler format)
  // Format: - **[name](link)** - $price\n  description
  const markdownPatternNoImage =
    /-\s*\*\*\[([^\]]+)\]\(([^)]+)\)\*\*\s*-\s*([^\n]+)\s*([^\n-]*)/g;
  const markdownMatchesNoImage = [
    ...innerContent.matchAll(markdownPatternNoImage),
  ];

  if (markdownMatchesNoImage.length > 0) {
    console.log(
      `✅ Found ${markdownMatchesNoImage.length} products in Markdown format (no images)`,
    );
    const products = markdownMatchesNoImage.map((match, idx) => {
      const product = {
        name: match[1].trim(), // Product name
        image: "", // No image
        link: match[2].trim(), // Product link
        price: match[3].trim(), // Price
        description: match[4]?.trim() || "", // Description
      };
      console.log(`📦 Product ${idx + 1}:`, product);
      return product;
    });
    return products;
  }

  // Extract from li tags as fallback
  const liPattern = /<li[^>]*>(.*?)<\/li>/g;
  const liMatches = [...innerContent.matchAll(liPattern)];

  if (liMatches.length === 0) {
    console.log("❌ No valid format found (JSON, Markdown, or li tags)");
    return [];
  }

  console.log(`🔍 Found ${liMatches.length} li tags`);

  // Try to parse each li content as JSON
  const products = liMatches
    .map((match, idx) => {
      try {
        const itemContent = match[1].trim();
        console.log(`🔍 Processing li ${idx}:`, itemContent);

        // Try to parse as JSON object
        if (itemContent.startsWith("{")) {
          const parsed = JSON.parse(itemContent);
          console.log(`✅ Parsed li ${idx}:`, parsed);
          return parsed;
        }
        // Otherwise return as plain URL/text
        return { url: itemContent };
      } catch (e) {
        console.log(`⚠️ Failed to parse li ${idx}:`, e);
        return { url: match[1].trim() };
      }
    })
    .filter((item) => item && (item.url || item.link || item.image));

  console.log("✅ Final products from li tags:", products);
  return products;
};

/**
 * Remove ecommerce-image tags and their content from the text
 * This prevents the ecommerce content from being rendered in the markdown
 */
export const removeEcommerceContent = (content: string): string => {
  if (!content || typeof content !== "string") {
    return content;
  }

  // Remove ecommerce-image tags (both formats: ecommerce-image and ecommerce_image)
  // Remove both complete and incomplete tags
  return content
    .replace(/<ecommerce[-_]image[^>]*>[\s\S]*?<\/ecommerce[-_]image>/gi, "")
    .replace(/<ecommerce[-_]image[^>]*>[\s\S]*/gi, "")
    .trim();
};

/**
 * Remove clickable-options-cbb tags and their content from the text
 * This prevents the clickable options from being rendered in the markdown (they're shown as buttons)
 */
export const removeClickableOptions = (content: string): string => {
  if (!content || typeof content !== "string") {
    return content;
  }

  // Remove clickable-options-cbb tags (both complete and incomplete)
  return content
    .replace(
      /<clickable-options-cbb[^>]*>[\s\S]*?<\/clickable-options-cbb>/gi,
      "",
    )
    .replace(/<clickable-options-cbb[^>]*>[\s\S]*/gi, "")
    .trim();
};

/**
 * Convert <br-cbb> tags to actual line breaks
 */
export const convertBrCbbToLineBreaks = (content: string): string => {
  if (!content || typeof content !== "string") {
    return content;
  }

  // Replace <br-cbb> with actual line breaks (double newline for markdown)
  return content.replace(/<br-cbb\s*\/?>/gi, "\n\n");
};

// Payment Button Utilities
export interface PaymentButtonData {
  url: string;
  order: string;
  amount: string;
  image?: string;
}

export const isPaymentButton = (content: string): boolean => {
  if (!content || typeof content !== "string") return false;
  return content.includes("<payment-button>");
};

export const isPaymentButtonComplete = (content: string): boolean => {
  if (!content || typeof content !== "string") return false;
  return (
    content.includes("<payment-button>") &&
    content.includes("</payment-button>")
  );
};

export const removePaymentButton = (content: string): string => {
  if (!content || typeof content !== "string") return content;

  // Remove complete payment-button blocks
  let result = content.replace(
    /<payment-button[^>]*>[\s\S]*?<\/payment-button>/g,
    "",
  );

  // Also remove incomplete payment-button blocks (streaming)
  result = result.replace(/<payment-button[^>]*>[\s\S]*$/g, "");

  return result;
};

export const extractPaymentButtonContent = (content: string): string | null => {
  if (!content || typeof content !== "string") return null;

  const pattern = /<payment-button[^>]*>(.*?)<\/payment-button>/s;
  const match = content.match(pattern);

  if (!match || !match[1]) {
    return null;
  }

  return match[1].trim();
};

/**
 * Parse payment button data from <payment-button> content
 * Supports two formats:
 * 1. Inline: <payment-button> url: {url} order: {order} amount: {amount} image: {image} </payment-button>
 * 2. Multi-line:
 *    <payment-button>
 *    url: {url}
 *    order: {order}
 *    amount: {amount}
 *    image: {image}
 *    </payment-button>
 */
export const parsePaymentButton = (
  content: string,
): PaymentButtonData | null => {
  if (!content || typeof content !== "string") return null;

  const buttonContent = extractPaymentButtonContent(content);
  console.log("[PaymentButton] Extracted content:", buttonContent);

  if (!buttonContent) return null;

  try {
    // More flexible regex patterns to handle various formats
    // Match URL - can have query parameters
    const urlMatch = buttonContent.match(
      /url:\s*([^\s]+[\S]*?)(?=\s+order:|\s+amount:|\s+image:|$)/i,
    );
    // Match order number
    const orderMatch = buttonContent.match(/order:\s*([^\s]+)/i);
    // Match amount - can include currency symbols
    const amountMatch = buttonContent.match(/amount:\s*([^\s<]+)/i);
    // Match image (optional) - can be a full URL
    const imageMatch = buttonContent.match(
      /image:\s*([^\s]+[\S]*?)(?=\s+url:|\s+order:|\s+amount:|$)/i,
    );

    console.log("[PaymentButton] Matches:", {
      urlMatch,
      orderMatch,
      amountMatch,
      imageMatch,
    });

    if (!urlMatch || !orderMatch || !amountMatch) {
      console.warn("[PaymentButton] Missing required fields");
      return null;
    }

    const result: PaymentButtonData = {
      url: urlMatch[1].trim(),
      order: orderMatch[1].trim(),
      amount: amountMatch[1].trim(),
    };

    // Add image if present
    if (imageMatch && imageMatch[1]) {
      result.image = imageMatch[1].trim();
    }

    console.log("[PaymentButton] Parsed result:", result);
    return result;
  } catch (error) {
    console.error("[PaymentButton] Error parsing payment button:", error);
    return null;
  }
};

export const removeCustomTags = (html: string): string => {
  if (!html || !html.trim()) return "";
  return html.replace(/<[^>]+>/g, "").trim();
};

export const checkOtpCollection = (
  content: string,
): { check: boolean; msg: string; contactExtract: string; type: string } => {
  if (!content || typeof content !== "string")
    return { check: false, msg: "", contactExtract: "", type: "" };
  const check =
    content.includes("<otp-collection>") &&
    content.includes("</otp-collection>");

  //extract email/phone sent after </otp-collection> tag in content
  const emailExtractMatch = content.match(/<\/otp-collection>(.+)$/);
  const contactExtract = emailExtractMatch ? emailExtractMatch[1].trim() : "";
  const type = contactExtract.includes("@")
    ? "email"
    : /^\+?\d{10,}$/.test(contactExtract.replace(/[\s-]/g, ""))
      ? "phone"
      : "email";
  // Extract only the message between tags
  const msgMatch = content.match(/<otp-collection>(.+?)<\/otp-collection>/);
  let msg = msgMatch
    ? msgMatch[1].trim()
    : content.replace(/<\/?otp-collection>/g, "").trim();
  msg = msg.replace(/<\/?resend>/gi, "").trim();
  return { check, msg, contactExtract, type };
};

export const renderHTML = (html: string) => {
  return { __html: html };
};

export function stripHTML(input) {
  if (typeof input !== "string") return "";
  return input.replace(/<[^>]*>/g, "").trim();
}

export const removeCustomTagsForPreview = (
  content: string,
  customTags: string[] = [
    "<br-cbb>",
    "<links-cbb>",
    "</links-cbb>",
    "<clickable-options-cbb>",
    "</clickable-options-cbb>",
    "<handoff>",
    "<ecommerce-image>",
    "</ecommerce-image>",
  ],
): string => {
  if (!content || !content.trim()) return "";

  let result = content;

  customTags.forEach((tag) => {
    const escapedTag = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(new RegExp(escapedTag, "gi"), "");
  });

  result = result.replace(/<\/?li>/gi, "");
  result = result.replace(/<br-cbb>/gi, " ");

  return result.trim();
};
